import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Box,
  Typography,
  useTheme,
  alpha,
  styled
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';
import { RevenueData } from '../../services/dashboardService';
import { formatXOF } from '../../utils/currency';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
}));

const CustomTooltip = ({ active, payload, label }: any) => {
  const theme = useTheme();
  
  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          p: 2,
          boxShadow: theme.customShadows.z8,
        }}
      >
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
          {new Date(label).toLocaleDateString('fr-FR', { 
            day: 'numeric', 
            month: 'short',
            year: 'numeric'
          })}
        </Typography>
        <Typography variant="body2" color="primary.main" sx={{ fontWeight: 600 }}>
          Revenue: {formatXOF(payload[0].value)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Shipments: {payload[0].payload.shipments}
        </Typography>
      </Box>
    );
  }
  return null;
};

interface RevenueChartProps {
  data: RevenueData[];
  title?: string;
  height?: number;
  showArea?: boolean;
}

export default function RevenueChart({ 
  data, 
  title = "Revenue Trends", 
  height = 300,
  showArea = true 
}: RevenueChartProps) {
  const theme = useTheme();

  const formatXAxisTick = (tickItem: string) => {
    const date = new Date(tickItem);
    return date.toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'short' 
    });
  };

  const formatYAxisTick = (value: number) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(0)}K`;
    }
    return value.toString();
  };

  const ChartComponent = showArea ? AreaChart : LineChart;
  const DataComponent = showArea ? Area : Line;

  return (
    <StyledCard>
      <CardHeader
        title={
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {title}
          </Typography>
        }
        subheader={
          <Typography variant="body2" color="text.secondary">
            Revenue trends over time (XOF)
          </Typography>
        }
        sx={{ pb: 1 }}
      />
      <CardContent sx={{ pt: 0 }}>
        <Box sx={{ width: '100%', height }}>
          <ResponsiveContainer width="100%" height="100%">
            <ChartComponent data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <defs>
                <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop 
                    offset="5%" 
                    stopColor={theme.palette.primary.main} 
                    stopOpacity={0.3}
                  />
                  <stop 
                    offset="95%" 
                    stopColor={theme.palette.primary.main} 
                    stopOpacity={0.05}
                  />
                </linearGradient>
              </defs>
              
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke={alpha(theme.palette.text.secondary, 0.1)}
                vertical={false}
              />
              
              <XAxis 
                dataKey="date"
                tickFormatter={formatXAxisTick}
                axisLine={false}
                tickLine={false}
                tick={{ 
                  fontSize: 12, 
                  fill: theme.palette.text.secondary 
                }}
              />
              
              <YAxis 
                tickFormatter={formatYAxisTick}
                axisLine={false}
                tickLine={false}
                tick={{ 
                  fontSize: 12, 
                  fill: theme.palette.text.secondary 
                }}
              />
              
              <Tooltip content={<CustomTooltip />} />
              
              {showArea ? (
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke={theme.palette.primary.main}
                  strokeWidth={3}
                  fill="url(#revenueGradient)"
                  dot={{ 
                    fill: theme.palette.primary.main, 
                    strokeWidth: 2,
                    stroke: theme.palette.background.paper,
                    r: 4
                  }}
                  activeDot={{ 
                    r: 6, 
                    fill: theme.palette.primary.main,
                    stroke: theme.palette.background.paper,
                    strokeWidth: 2
                  }}
                />
              ) : (
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke={theme.palette.primary.main}
                  strokeWidth={3}
                  dot={{ 
                    fill: theme.palette.primary.main, 
                    strokeWidth: 2,
                    stroke: theme.palette.background.paper,
                    r: 4
                  }}
                  activeDot={{ 
                    r: 6, 
                    fill: theme.palette.primary.main,
                    stroke: theme.palette.background.paper,
                    strokeWidth: 2
                  }}
                />
              )}
            </ChartComponent>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </StyledCard>
  );
}
